// DashboardPermissionDialog.tsx
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import RoleOptionsDropdown from "../internalUserRoles/RoleOptionsDropdown";
import {
  DashboardReportPermission,
  DashboardReportType,
  InternalUserDepartment,
  InternalUserRoleType,
  InternalUserTeam
} from "../types/app";

interface Props {
  open: boolean;
  onClose: () => void;
  permission: DashboardReportPermission | null;
}

export default function DashboardPermissionDialog({ open, onClose, permission }: Props) {
  const [formData, setFormData] = useState<DashboardReportPermission>({
    id: 0,
    internalUserRoleTypeId: 0,
    internalUserDepartmentId: 0,
    internalUserTeamId: 0,
    dashboardReportTypeId: 0
  });

  const [teamOptions, setTeamOptions] = useState<InternalUserTeam[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<InternalUserDepartment[]>([]);
  const [roleTypeOptions, setRoleTypeOptions] = useState<InternalUserRoleType[]>([]);
  const [reportTypeOptions, setReportTypeOptions] = useState<DashboardReportType[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  const { enqueueSnackbar } = useSnackbar();

  const isEditing = !!permission;

  useEffect(() => {
    if (permission) {
      setFormData({
        id: permission.id,
        internalUserRoleTypeId: permission.internalUserRoleTypeId || 0,
        internalUserDepartmentId: permission.internalUserDepartmentId || 0,
        internalUserTeamId: permission.internalUserTeamId || 0,
        dashboardReportTypeId: permission.dashboardReportTypeId
      });
    } else {
      setFormData({
        id: 0,
        internalUserRoleTypeId: 0,
        internalUserDepartmentId: 0,
        internalUserTeamId: 0,
        dashboardReportTypeId: 0
      });
    }
    setErrors({});
  }, [permission, open]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [roleTypes, teams, departments, dashboardReportTypes] = await Promise.all([
          axios.get<InternalUserRoleType[]>(`/api/clientphonebook/internalusers/getInternalUserRoleTypeOptions`),
          axios.get<InternalUserTeam[]>(`/api/clientphonebook/internalusers/getInternalUserTeamOptions`),
          axios.get<InternalUserDepartment[]>(`/api/clientphonebook/internalusers/getInternalUserDepartmentOptions`),
          axios.get<InternalUserDepartment[]>(`/api/clientphonebook/dashboardReportPermissions/getDashboardReportTypes`)
        ]);

        setRoleTypeOptions([{ id: 0, name: "All Roles" }, ...roleTypes.data]);
        setTeamOptions([{ id: 0, name: "All Teams" }, ...teams.data]);
        setDepartmentOptions([{ id: 0, name: "All Departments" }, ...departments.data]);
        setReportTypeOptions(dashboardReportTypes.data);
      } catch (error) {
        enqueueSnackbar("Error loading options", { variant: "error" });
      }
    };

    if (open) {
      loadOptions();
    }
  }, [open]);

  const handleChange = (key: keyof DashboardReportPermission, value: number) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (value !== 0) {
      setErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSave = async () => {
    // Validate required fields
    const newErrors: { [key: string]: boolean } = {};
    if (formData.dashboardReportTypeId === 0) {
      newErrors.dashboardReportTypeId = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        ...formData,
        internalUserRoleTypeId: formData.internalUserRoleTypeId || null,
        internalUserDepartmentId: formData.internalUserDepartmentId || null,
        internalUserTeamId: formData.internalUserTeamId || null
      };

      if (isEditing) {
        await axios.post(`/api/clientphonebook/dashboardReportPermissions/EditReportPermissionRequest`, payload);
        enqueueSnackbar("Permission updated successfully", { variant: "success" });
      } else {
        await axios.post(`/api/clientphonebook/dashboardReportPermissions/AddReportPermissionRequest`, payload);
        enqueueSnackbar("Permission added successfully", { variant: "success" });
      }

      onClose();
    } catch (error) {
      enqueueSnackbar("Error saving permission", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? "Edit" : "Add"} Dashboard Report Permission</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave role, department, or team blank to apply to all users in that category.
        </Typography>

        <RoleOptionsDropdown
          sx={{ minWidth: 200, mr: 2, mb: 2, width: "100%" }}
          label="Report Type"
          value={formData.dashboardReportTypeId}
          options={reportTypeOptions}
          onChange={(e) => handleChange("dashboardReportTypeId", e.target.value as number)}
          error={errors.dashboardReportTypeId}
        />

        <RoleOptionsDropdown
          sx={{ minWidth: 200, mr: 2, mb: 2, width: "100%" }}
          label="Department"
          value={formData.internalUserDepartmentId || 0}
          options={departmentOptions}
          onChange={(e) => handleChange("internalUserDepartmentId", e.target.value as number)}
          error={errors.internalUserDepartmentId}
        />

        <RoleOptionsDropdown
          sx={{ minWidth: 200, mr: 2, mb: 2, width: "100%" }}
          label="Team"
          value={formData.internalUserTeamId || 0}
          options={teamOptions}
          onChange={(e) => handleChange("internalUserTeamId", e.target.value as number)}
          error={errors.internalUserTeamId}
        />

        <RoleOptionsDropdown
          sx={{ minWidth: 200, mr: 2, mb: 2, width: "100%" }}
          label="Role Type"
          value={formData.internalUserRoleTypeId || 0}
          options={roleTypeOptions}
          onChange={(e) => handleChange("internalUserRoleTypeId", e.target.value as number)}
          error={errors.internalUserRoleTypeId}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {isEditing ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
